import React from 'react';
import '../styles/companion-views.css';

/**
 * CompanionEvidenceDetails - Shows detailed information about the current evidence
 * Used when viewing job/order lists to provide evidence context
 */
const CompanionEvidenceDetails = ({ data = {} }) => {
  const evidence = data.evidence || {};
  
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="companion-evidence-details">
      {/* Evidence Header */}
      <div className="evidence-header-section">
        <div className="evidence-icon">
          {evidence.type === 'video' && '🎥'}
          {evidence.type === 'image' && '🖼️'}
          {evidence.type === 'document' && '📄'}
          {evidence.type === 'audio' && '🎵'}
          {!evidence.type && '📁'}
        </div>
        <div className="evidence-title-info">
          <h4>{evidence.name || 'Untitled Evidence'}</h4>
          <span className="evidence-id">{evidence.id}</span>
        </div>
      </div>

      {/* Evidence Metadata */}
      <div className="evidence-metadata-section">
        <h5>File Information</h5>
        <div className="metadata-grid">
          <div className="metadata-item">
            <span className="metadata-label">Type:</span>
            <span className="metadata-value">{evidence.type || 'Unknown'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Format:</span>
            <span className="metadata-value">{evidence.format || 'Unknown'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Size:</span>
            <span className="metadata-value">{formatFileSize(evidence.size)}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Uploaded:</span>
            <span className="metadata-value">{formatDate(evidence.uploadDate)}</span>
          </div>
          {evidence.duration && (
            <div className="metadata-item">
              <span className="metadata-label">Duration:</span>
              <span className="metadata-value">{evidence.duration}</span>
            </div>
          )}
          {evidence.resolution && (
            <div className="metadata-item">
              <span className="metadata-label">Resolution:</span>
              <span className="metadata-value">{evidence.resolution}</span>
            </div>
          )}
        </div>
      </div>

      {/* Processing Status */}
      {evidence.processingStatus && (
        <div className="evidence-processing-section">
          <h5>Processing Status</h5>
          <div className="processing-status">
            <div className="status-indicator">
              <span className={`status-dot ${evidence.processingStatus}`}></span>
              <span className="status-text">
                {evidence.processingStatus === 'completed' && 'Fully Processed'}
                {evidence.processingStatus === 'processing' && 'Processing...'}
                {evidence.processingStatus === 'pending' && 'Pending'}
                {evidence.processingStatus === 'failed' && 'Failed'}
              </span>
            </div>
            {evidence.processingProgress && (
              <div className="processing-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${evidence.processingProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{evidence.processingProgress}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analysis Summary */}
      {evidence.analysisSummary && (
        <div className="evidence-analysis-section">
          <h5>Analysis Summary</h5>
          <div className="analysis-summary">
            {evidence.analysisSummary.totalJobs && (
              <div className="summary-item">
                <span className="summary-icon">📊</span>
                <span className="summary-text">
                  {evidence.analysisSummary.totalJobs} total analysis jobs
                </span>
              </div>
            )}
            {evidence.analysisSummary.completedJobs !== undefined && (
              <div className="summary-item">
                <span className="summary-icon">✅</span>
                <span className="summary-text">
                  {evidence.analysisSummary.completedJobs} completed
                </span>
              </div>
            )}
            {evidence.analysisSummary.detectedObjects && (
              <div className="summary-item">
                <span className="summary-icon">🎯</span>
                <span className="summary-text">
                  {evidence.analysisSummary.detectedObjects} objects detected
                </span>
              </div>
            )}
            {evidence.analysisSummary.confidence && (
              <div className="summary-item">
                <span className="summary-icon">📈</span>
                <span className="summary-text">
                  {evidence.analysisSummary.confidence}% avg confidence
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tags */}
      {evidence.tags && evidence.tags.length > 0 && (
        <div className="evidence-tags-section">
          <h5>Tags</h5>
          <div className="tags-container">
            {evidence.tags.map((tag, index) => (
              <span key={index} className="evidence-tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Related Items */}
      {data.relatedItems && data.relatedItems.length > 0 && (
        <div className="evidence-related-section">
          <h5>Related Items</h5>
          <div className="related-items-list">
            {data.relatedItems.map((item, index) => (
              <div key={index} className="related-item">
                <span className="related-item-icon">
                  {item.type === 'job' && '💼'}
                  {item.type === 'order' && '📋'}
                  {item.type === 'evidence' && '📁'}
                </span>
                <span className="related-item-name">{item.name}</span>
                <span className="related-item-count">{item.count || 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {data.actions && data.actions.length > 0 && (
        <div className="evidence-actions-section">
          {data.actions.map((action, index) => (
            <button
              key={index}
              className={`evidence-action-btn ${action.type || 'default'}`}
              onClick={action.onClick}
            >
              {action.icon && <span>{action.icon}</span>}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanionEvidenceDetails;
